/**
 * GOOGLE MAPS FRONTEND INTEGRATION - ESSENTIAL GUIDE
 *
 * USAGE FROM PARENT COMPONENT:
 * ======
 *
 * const mapRef = useRef<google.maps.Map | null>(null);
 *
 * <MapView
 *   initialCenter={{ lat: 40.7128, lng: -74.0060 }}
 *   initialZoom={15}
 *   onMapReady={(map) => {
 *     mapRef.current = map; // Store to control map from parent anytime, google map itself is in charge of the re-rendering, not react state.
 * </MapView>
 *
 * ======
 * Available Libraries and Core Features:
 * -------------------------------
 * 📍 MARKER (from `marker` library)
 * - Attaches to map using { map, position }
 * new google.maps.marker.AdvancedMarkerElement({
 *   map,
 *   position: { lat: 37.7749, lng: -122.4194 },
 *   title: "San Francisco",
 * });
 *
 * -------------------------------
 * 🏢 PLACES (from `places` library)
 * - Does not attach directly to map; use data with your map manually.
 * const place = new google.maps.places.Place({ id: PLACE_ID });
 * await place.fetchFields({ fields: ["displayName", "location"] });
 * map.setCenter(place.location);
 * new google.maps.marker.AdvancedMarkerElement({ map, position: place.location });
 *
 * -------------------------------
 * 🧭 GEOCODER (from `geocoding` library)
 * - Standalone service; manually apply results to map.
 * const geocoder = new google.maps.Geocoder();
 * geocoder.geocode({ address: "New York" }, (results, status) => {
 *   if (status === "OK" && results[0]) {
 *     map.setCenter(results[0].geometry.location);
 *     new google.maps.marker.AdvancedMarkerElement({
 *       map,
 *       position: results[0].geometry.location,
 *     });
 *   }
 * });
 *
 * -------------------------------
 * 📐 GEOMETRY (from `geometry` library)
 * - Pure utility functions; not attached to map.
 * const dist = google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
 *
 * -------------------------------
 * 🛣️ ROUTES (from `routes` library)
 * - Combines DirectionsService (standalone) + DirectionsRenderer (map-attached)
 * const directionsService = new google.maps.DirectionsService();
 * const directionsRenderer = new google.maps.DirectionsRenderer({ map });
 * directionsService.route(
 *   { origin, destination, travelMode: "DRIVING" },
 *   (res, status) => status === "OK" && directionsRenderer.setDirections(res)
 * );
 *
 * -------------------------------
 * 🌦️ MAP LAYERS (attach directly to map)
 * - new google.maps.TrafficLayer().setMap(map);
 * - new google.maps.TransitLayer().setMap(map);
 * - new google.maps.BicyclingLayer().setMap(map);
 *
 * -------------------------------
 * ✅ SUMMARY
 * - “map-attached” → AdvancedMarkerElement, DirectionsRenderer, Layers.
 * - “standalone” → Geocoder, DirectionsService, DistanceMatrixService, ElevationService.
 * - “data-only” → Place, Geometry utilities.
 */

/// <reference types="@types/google.maps" />

import React, { useEffect, useRef, useState } from "react";
import { usePersistFn } from "@/hooks/usePersistFn";
import { cn } from "@/lib/utils";
import { classifyMapsFrontendFailure, loadMapsFrontendScript, subscribeMapsAuthenticationFailure, type MapsFrontendFailure } from "@/lib/mapsFrontendLoader";

declare global {
  interface Window {
    google?: typeof google;
  }
}

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY;
const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

function loadMapScript() {
  return loadMapsFrontendScript(API_KEY, MAP_ID);
}

interface MapViewProps {
  className?: string;
  initialCenter?: google.maps.LatLngLiteral;
  initialZoom?: number;
  onMapReady?: (map: google.maps.Map) => void;
  onMapError?: (kind: MapsFrontendFailure) => void;
  children?: (map: google.maps.Map) => React.ReactNode;
}

export function MapView({
  className,
  initialCenter = { lat: 37.7749, lng: -122.4194 },
  initialZoom = 12,
  onMapReady,
  onMapError,
  children,
}: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapHomeSlot = useRef<HTMLDivElement>(null);
  const [readyMap, setReadyMap] = useState<google.maps.Map | null>(null);
  const onMapReadyLatest = usePersistFn((map: google.maps.Map) => onMapReady?.(map));
  const onMapErrorLatest = usePersistFn((failure: MapsFrontendFailure) => onMapError?.(failure));

  useEffect(() => {
    let active = true;
    let authenticationFailure = false;
    let idleListener: google.maps.MapsEventListener | undefined;
    let sizeObserver: ResizeObserver | undefined;
    const stopReadiness = () => { idleListener?.remove(); idleListener = undefined; sizeObserver?.disconnect(); sizeObserver = undefined; };
    const unsubscribeAuthentication = subscribeMapsAuthenticationFailure(() => {
      if (!active || authenticationFailure) return;
      authenticationFailure = true;
      stopReadiness();
      setReadyMap(null);
      onMapErrorLatest("authentication");
    });

    void (async () => {
      try {
        await loadMapScript();
      } catch (error) {
        if (active) onMapErrorLatest(classifyMapsFrontendFailure(error));
        return;
      }
      if (!active || authenticationFailure) return;
      const maps = window.google?.maps;
      if (!maps?.Map) {
        onMapErrorLatest("initialization");
        return;
      }
      const slot = mapHomeSlot.current;
      if (!mapContainer.current && slot) {
        const container = document.createElement("div");
        container.className = "absolute inset-0 h-full w-full";
        container.setAttribute("data-google-map-surface", "true");
        slot.appendChild(container);
        mapContainer.current = container;
      }
      const container = mapContainer.current;
      if (!container) {
        onMapErrorLatest("initialization");
        return;
      }
      try {
        const map = new maps.Map(container, {
          zoom: initialZoom,
          center: initialCenter,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true,
          streetViewControl: true,
          mapId: MAP_ID,
        });
        if (active && !authenticationFailure) {
          // Constructor completion is not renderer readiness (notably during
          // vector → raster fallback). Configure the map now, but expose it to
          // drawing children only after the first documented idle/render cycle.
          let rendered = false;
          const publish = () => {
            const rect = container.getBoundingClientRect();
            if (!active || authenticationFailure || !rendered || !container.isConnected || mapContainer.current !== container || rect.width <= 0 || rect.height <= 0) return;
            stopReadiness();
            setReadyMap(map);
          };
          idleListener = map.addListener("idle", () => { rendered = true; publish(); });
          sizeObserver = new ResizeObserver(publish);
          sizeObserver.observe(container);
          onMapReadyLatest(map);
        }
      } catch {
        stopReadiness();
        container.remove();
        if (mapContainer.current === container) mapContainer.current = null;
        if (active) onMapErrorLatest("initialization");
      }
    })();

    return () => {
      active = false;
      stopReadiness();
      unsubscribeAuthentication();
      mapContainer.current?.remove();
      mapContainer.current = null;
    };
  }, [onMapErrorLatest, onMapReadyLatest]);

  return (
    <div className="relative w-full">
      <div ref={mapHomeSlot} data-google-map-home-slot="true" className={cn("relative w-full h-[500px] overflow-hidden", className)} />
      {readyMap && children?.(readyMap)}
    </div>
  );
}
